import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'

export const formatAmountToBigNumber = (
  amount: string = '0',
  decimals: number = 18,
) => {
  const destAmount = new BigNumber(amount).multipliedBy(10 ** decimals)
  return destAmount
}

export const formatBigNumberToAmount = (
  amount: string = '0',
  decimals: number = 18,
) => {
  const destAmount = new BigNumber(amount).dividedBy(10 ** decimals)
  return destAmount
}

export const paraErrorMessageFormatter = (
  message: string,
  network: any,
  defaultMessage?: string,
) => {
  switch (message) {
    case "It seems like your wallet doesn't contain enough BNB to cover the gas fees.":
      return `Insufficient ${network?.symbol} for gas fees`
    case 'insufficient_balance':
      return `Insufficient ${network?.symbol} balance`
    case 'INVALID':
      return 'Invalid receiver address'
    case 'MISSING':
      return 'Missing receiver address'
    case '100':
      return 'Please enter input amount'
    case 'This transaction has some errors and may fail. Please contact support for more details':
      return `Transaction error with ${network?.symbol}`
    case "It seems like your wallet doesn't contain enough ETH to cover the gas fees.":
      return `Insufficient ${network?.symbol} for gas fees`
    case "It seems like your wallet doesn't contain enough MATIC to cover the gas fees.":
      return `Insufficient ${network?.symbol} for gas fees`
    case 'Returned error: insufficient funds for gas * price + value':
      return `Insufficient ${network?.symbol} for gas fees`
    case 'The rate has changed, please re-query the latest Price':
      return 'The rate has changed, please re-enter amount'
    case 'No transaction object given!':
      return 'Not enough liquidity at the moment'
    default:
      if (message.includes('insufficient funds')) {
        return `Insufficient ${network?.symbol} for gas fees`
      } else if (defaultMessage) {
        return defaultMessage
      }
      return 'Something went wrong'
  }
}

export const addressPreCheck = (address: string) => {
  if (address === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
    return 'native'
  } else {
    return address
  }
}

// amount must be a BigNumber, {base,display}Decimals must be Numbers
export function amountFormatter(
  amount: any,
  bDecimals = 18,
  displayDecimals = 8,
  useLessThan = true,
) {
  const baseDecimals = bDecimals ?? 18

  displayDecimals = Math.min(displayDecimals, baseDecimals)
  if (
    baseDecimals > 18 ||
    displayDecimals > 18 ||
    displayDecimals > baseDecimals
  ) {
    throw Error(
      `Invalid combination of baseDecimals '${baseDecimals}' and displayDecimals '${displayDecimals}.`,
    )
  }

  // if balance is falsy, return undefined
  if (!amount) {
    return ''
  }
  // if amount is 0, return
  else if (Number(amount) === 0) {
    return '0'
  }
  // amount > 0
  else {
    // amount of 'wei' in 1 'ether'
    const baseAmount = ethers.BigNumber.from(10).pow(
      ethers.BigNumber.from(baseDecimals),
    )

    const minimumDisplayAmount = baseAmount.div(
      ethers.BigNumber.from(10).pow(ethers.BigNumber.from(displayDecimals)),
    )

    // if balance is less than the minimum display amount
    if (amount.lt(minimumDisplayAmount)) {
      return useLessThan
        ? `<${ethers.utils.formatUnits(minimumDisplayAmount, baseDecimals)}`
        : `${ethers.utils.formatUnits(amount, baseDecimals)}`
    }
    // if the balance is greater than the minimum display amount
    else {
      const stringAmount = ethers.utils.formatUnits(amount, baseDecimals)

      // if there isn't a decimal portion
      if (!stringAmount.match(/\./)) {
        return stringAmount
      }
      // if there is a decimal portion
      else {
        const [wholeComponent, decimalComponent] = stringAmount.split('.')
        const roundedDecimalComponent = ethers.BigNumber.from(
          decimalComponent.padEnd(baseDecimals, '0'),
        )
          .toString()
          .padStart(baseDecimals, '0')
          .substring(0, displayDecimals)

        // decimals are too small to show
        if (roundedDecimalComponent === '0'.repeat(displayDecimals)) {
          return wholeComponent
        }
        // decimals are not too small to show
        else {
          return `${wholeComponent}.${roundedDecimalComponent
            .toString()
            .replace(/0*$/, '')}`
        }
      }
    }
  }
}
